import { Vector3 } from "@babylonjs/core";
import type { ShopUnitZone } from "./zones/ShopUnitZone.js";

// Measured from the unit's door (local z=0). With UNIT_DEPTH=25 the back wall
// sits ~30 m from the door, so the load radius must exceed that to keep a
// unit's interior resident while the player is deep inside it.
const LOAD_RADIUS: number = 34;
const UNLOAD_RADIUS: number = 50;
const CHECK_INTERVAL_MS: number = 400;
const MAX_CONCURRENT_LOADS: number = 3;

/**
 * Proximity streaming for shop interiors. Shells and signage are always
 * resident; interiors (fixtures + product textures) load when the player
 * nears a unit's door and unload with hysteresis when they walk away. This
 * is what keeps ~40 stores viable on a phone.
 */
export class StreamingManager {
  private readonly units: ShopUnitZone[];
  private readonly playerPosition: () => Vector3;
  private lastCheck: number = 0;
  private loading: number = 0;

  constructor(units: ShopUnitZone[], playerPosition: () => Vector3) {
    this.units = units;
    this.playerPosition = playerPosition;
  }

  /** Call once per render tick; internally throttled. */
  update(nowMs: number): void {
    if (nowMs - this.lastCheck < CHECK_INTERVAL_MS) {
      return;
    }
    this.lastCheck = nowMs;
    const pos: Vector3 = this.playerPosition();

    for (const unit of this.units) {
      if (unit.shop === null) {
        continue;
      }
      const door: Vector3 = unit.doorWorldPosition();
      const dx: number = pos.x - door.x;
      const dz: number = pos.z - door.z;
      const distSq: number = dx * dx + dz * dz;

      if (distSq <= LOAD_RADIUS * LOAD_RADIUS) {
        if (!unit.isInteriorLoaded() && this.loading < MAX_CONCURRENT_LOADS) {
          this.loading++;
          void unit
            .loadInterior()
            .finally(() => {
              this.loading--;
            });
        }
      } else if (distSq >= UNLOAD_RADIUS * UNLOAD_RADIUS) {
        if (unit.isInteriorLoaded()) {
          unit.unloadInterior();
        }
      }
    }
  }
}
